import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import LightSelect from "../../CompanyView/LightSelect";
import { UseCompanyWorkspaces } from "../../../hooks/company";
import { isKpiScorekeeperOnly } from "../../../constants/kpi";
import { WINDOWS } from "../../../helpers/scopedWindows";
import {
  autoMetricColumns,
  mpKeyFromLabel,
  mpKindOf,
  mpNameKey,
  sanitizeSourceWindow,
} from "../../../helpers/metricPlan";
import "./customize.css";

/**
 * The "which metrics combine" builder for a cross-workspace card.
 *
 * Off (Automatic), the card matches metrics by name — a column appears when
 * every chosen workspace tracks a metric with the same title. Switch to Custom
 * and each column names, PER WORKSPACE, which metric feeds it (so differently
 * named metrics pair into one column) and whether the workspaces are averaged
 * or summed. This writes `draft.metricPlan`; the leaderboard, the Yeti tiles and
 * custom widgets all read it the same way (see helpers/metricPlan.js).
 */

const kindLabel = { $: "$", "%": "%", "#": "#" };

const WINDOW_OPTIONS = [
  { value: "follow", label: "Follow dashboard" },
  ...WINDOWS.map((w) => ({ value: w.id, label: w.label })),
];

const SourceWindowField = ({ value, onChange }) => {
  const win = value?.window || "follow";
  const custom = value?.customRange || {};
  const setWin = (nextWin) => {
    if (nextWin === "follow") {
      onChange(null);
      return;
    }
    if (nextWin === "custom") {
      onChange({ window: "custom", customRange: { ...custom } });
      return;
    }
    onChange({ window: nextWin });
  };
  return (
    <div className="gmbw-mp-win">
      <LightSelect
        width="100%"
        height={32}
        fontSize={12}
        radius={8}
        menuWidth={280}
        value={win}
        options={WINDOW_OPTIONS}
        onChange={(o) => setWin(o.value)}
      />
      {win === "custom" && (
        <div className="gmbw-range gmbw-mp-win-range">
          <input
            type="date"
            aria-label="From"
            max={new Date().toISOString().slice(0, 10)}
            value={custom.from || ""}
            onChange={(e) => onChange({ window: "custom", customRange: { ...custom, from: e.target.value } })}
          />
          <span className="gmbw-range-sep">→</span>
          <input
            type="date"
            aria-label="To"
            max={new Date().toISOString().slice(0, 10)}
            value={custom.to || ""}
            onChange={(e) => onChange({ window: "custom", customRange: { ...custom, to: e.target.value } })}
          />
        </div>
      )}
    </div>
  );
};

SourceWindowField.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};
SourceWindowField.defaultProps = { value: null };

const MetricPlanEditor = ({ draft, onChange, minSeedCols = 1, metricCatalog = [] }) => {
  const workspaces = UseCompanyWorkspaces(true) || [];
  const companyKpis = useSelector((s) => s.kpiReducer.companyKpis);

  const ids = useMemo(
    () => (draft.workspaceIds || []).filter(Boolean).map(String),
    [draft.workspaceIds],
  );

  /* Analytics dashboards build metricCatalog locally; Redux companyKpis only
     fills after the homepage KPI fetch. Merge both so pickers work everywhere. */
  const catalogDefsByWs = useMemo(() => {
    const map = {};
    (metricCatalog || []).forEach((m) => {
      if (!m) return;
      const label = (m.label || "").trim();
      if (!label) return;
      const wids = Array.isArray(m.workspaceIds) ? m.workspaceIds.map(String).filter(Boolean) : [];
      const kpiIds = Array.isArray(m.ids) && m.ids.length
        ? m.ids.map(String).filter(Boolean)
        : [String(m.id || "")].filter(Boolean);
      wids.forEach((wsId, idx) => {
        const kpiId = kpiIds[idx] || kpiIds[0];
        if (!kpiId) return;
        if (!map[wsId]) map[wsId] = [];
        if (!map[wsId].some((k) => String(k._id) === kpiId)) {
          map[wsId].push({ _id: kpiId, title: label, name: label, kind: m.kind });
        }
      });
    });
    return map;
  }, [metricCatalog]);

  /* Every real metric a workspace tracks (not just the live tiles) — pairing an
     odd-one-out needs the full list, not the ≤5 badge set. Scorekeeper-only
     metrics are never a valid column source, though — they must not appear on
     any board outside Scorekeeper, so the picker can't offer them at all. */
  const perWs = useMemo(
    () => ids.map((id) => {
      const fromRedux = ((companyKpis && companyKpis[id]) || [])
        .filter((k) => k && !k.isDraft && !k.disabled && (k.title || k.name) && !isKpiScorekeeperOnly(k));
      const fromCatalog = (catalogDefsByWs[id] || [])
        .filter((k) => k && (k.title || k.name) && !isKpiScorekeeperOnly(k));
      const merged = [...fromRedux];
      fromCatalog.forEach((k) => {
        if (!merged.some((x) => String(x._id) === String(k._id))) merged.push(k);
      });
      return {
        id,
        name: (workspaces.find((w) => String(w._id) === id) || {}).name || "Workspace",
        defs: merged,
      };
    }),
    [ids, workspaces, companyKpis, catalogDefsByWs],
  );

  /* Which tab the editor itself is showing — driven by the plan's own `on`
     flag, NOT hasMetricPlan(). hasMetricPlan also requires cols.length, so
     using it here snapped the editor back to Automatic (and repainted the
     auto-matched columns) the instant a Custom card's last column was
     removed — removing your only column looked like it silently switched
     modes and brought back other metrics. Custom with zero columns is still
     Custom; hasMetricPlan is for OTHER consumers deciding whether a plan is
     usable, not for which tab this editor highlights. */
  const custom = !!(draft.metricPlan && draft.metricPlan.on);
  /* Columns typed into Custom, remembered even while Automatic is showing —
     switching to Automatic only flips `on` off, it never discards `cols`, so
     flipping back to Custom restores exactly what was there instead of
     reseeding from the auto (name-matched) columns and silently losing edits. */
  const remembered = Array.isArray(draft.metricPlan?.cols) && draft.metricPlan.cols.length
    ? draft.metricPlan.cols
    : null;
  const cols = useMemo(() => {
    if (custom) return draft.metricPlan.cols || [];
    return autoMetricColumns(perWs.map((w) => ({ id: w.id, defs: w.defs })), true);
  }, [custom, draft.metricPlan, perWs]);

  /* Keep the IN-PROGRESS columns verbatim — do NOT sanitize here. Sanitizing on
     every keystroke dropped a column the instant it was added (no source yet),
     so "Add a column" appeared to do nothing. The plan is sanitized once, on
     Save (cleanSettingsFrom → layoutStore → the server), where empties belong. */
  const write = (nextCols) => {
    onChange({ ...draft, metricPlan: { on: true, cols: nextCols } });
  };

  const setAutomatic = () => {
    onChange({ ...draft, metricPlan: remembered ? { ...draft.metricPlan, on: false } : null });
  };

  const blankCol = (i = 0) => ({
    key: mpKeyFromLabel(`col-${Date.now().toString(36)}-${i}-${Math.random().toString(36).slice(2, 5)}`),
    label: "",
    /* Annual seed (minSeedCols≥2) defaults to $ + sum — revenue rollups.
       Yeti / leaderboard blank columns stay # + avg. */
    kind: Number(minSeedCols) >= 2 ? "$" : "#",
    mode: Number(minSeedCols) >= 2 ? "sum" : "avg",
    show: true,
    sources: {},
  });

  /* Switching to Custom restores whatever was last typed there, if anything —
     otherwise it seeds from the current auto columns so the admin edits the
     board as it stands; if the workspaces share no metric by name either,
     start with minSeedCols blank columns (annual comp wants ≥2 revenue slots). */
  const startCustom = () => {
    if (remembered) {
      write(remembered.map((c) => ({ ...c })));
      return;
    }
    if (cols.length) {
      write(cols.map((c) => ({ ...c })));
      return;
    }
    const n = Math.max(1, Number(minSeedCols) || 1);
    write(Array.from({ length: n }, (_, i) => blankCol(i)));
  };

  const patchCol = (i, patch) => write(cols.map((c, j) => (j === i ? { ...c, ...patch } : c)));

  /* Picking a source also adopts that metric's unit. The column used to keep
     whatever kind it was seeded with, so repointing a "Closed Rate" column at
     Sold Revenue left the editor showing "%" on a money column (the board
     itself already prefers the real KPI's kind — see effectiveMetricColumns —
     so the two disagreed on screen). The kind dropdown still overrides. */
  const pickSource = (i, wsId, kpiId) => {
    const col = cols[i];
    const sources = { ...(col.sources || {}), [wsId]: kpiId };
    const def = kpiId
      && (perWs.find((w) => w.id === wsId)?.defs || []).find((k) => String(k._id) === String(kpiId));
    const kind = def ? mpKindOf(def) : null;
    patchCol(i, {
      sources,
      ...(kind && kind !== col.kind ? { kind } : {}),
    });
  };
  const patchSourceWindow = (colIdx, wsId, sw) => {
    const col = cols[colIdx];
    const next = { ...(col.sourceWindows || {}) };
    const clean = sw ? sanitizeSourceWindow(sw) : null;
    if (!clean || clean.window === "follow") delete next[String(wsId)];
    else next[String(wsId)] = clean;
    patchCol(colIdx, { sourceWindows: Object.keys(next).length ? next : undefined });
  };
  const removeCol = (i) => write(cols.filter((_, j) => j !== i));
  const addCol = () => write([...cols, blankCol()]);

  if (ids.length < 2) {
    return (
      <div className="gmbw-fld">
        <label>Metrics</label>
        <p className="gmbw-hint">Pick two or more workspaces above, then choose which of their metrics line up into each column.</p>
      </div>
    );
  }

  const metricOptions = (wsId) => [
    { value: "", label: "— none —" },
    ...(perWs.find((w) => w.id === wsId)?.defs || []).map((k) => ({
      value: String(k._id),
      label: (k.title || k.name || "").trim(),
    })),
  ];

  /* When a source is picked but the column has no label yet, suggest the
     metric's own name so the admin rarely has to type. */
  const suggestLabel = (col) => {
    if (col.label) return col.label;
    for (let i = 0; i < perWs.length; i += 1) {
      const kpiId = col.sources?.[perWs[i].id];
      const hit = kpiId && perWs[i].defs.find((k) => String(k._id) === String(kpiId));
      if (hit) return (hit.title || hit.name || "").trim();
    }
    return "";
  };

  return (
    <div className="gmbw-fld">
      <label>Metrics</label>
      <div className="gmbw-seg" style={{ marginBottom: 8 }}>
        <button type="button" className={`gmbw-segbtn${!custom ? " on" : ""}`} onClick={setAutomatic}>
          Automatic
        </button>
        <button type="button" className={`gmbw-segbtn${custom ? " on" : ""}`} onClick={startCustom}>
          Custom
        </button>
      </div>

      {!custom ? (
        <>
          <p className="gmbw-hint">
            {cols.length
              ? `Matched by name across workspaces. Switch to Custom to pick a different metric per workspace or sum instead of average.`
              : "These workspaces track no metric with the same name — use Custom to pair them by hand."}
          </p>
          {cols.length > 0 && (
            <div className="gmbw-mp gmbw-mp-auto">
              {cols.map((col) => (
                <div className="gmbw-mp-col" key={col.key || col.label}>
                  <div className="gmbw-mp-row">
                    <span className="gmbw-mp-label ro">{col.label}</span>
                    <span className="gmbw-mp-kind ro">{kindLabel[col.kind] || "#"}</span>
                  </div>
                  {perWs.map((w) => {
                    const kpiId = col.sources?.[w.id];
                    const hit = kpiId && w.defs.find((k) => String(k._id) === String(kpiId));
                    return (
                      <div className="gmbw-mp-src" key={w.id}>
                        <span className="gmbw-mp-ws">{w.name}</span>
                        <span className="gmbw-mp-roval">{hit ? (hit.title || hit.name) : "— none —"}</span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
          <button type="button" className="gmbw-mp-add" onClick={startCustom}>
            Custom — pick metrics per workspace
          </button>
        </>
      ) : (
        <div className="gmbw-mp">
          {cols.map((col, i) => (
            <div className="gmbw-mp-col" key={col.key || i}>
              <div className="gmbw-mp-row">
                <input
                  className="gmbw-mp-label"
                  type="text"
                  value={col.label}
                  placeholder={suggestLabel(col) || "Column name"}
                  onChange={(e) => patchCol(i, { label: e.target.value })}
                />
                <div className="gmbw-mp-kind">
                  <LightSelect
                    width="72px"
                    height={34}
                    fontSize={12.5}
                    radius={8}
                    value={col.kind || "#"}
                    options={["#", "$", "%"].map((k) => ({ value: k, label: kindLabel[k] }))}
                    onChange={(o) => patchCol(i, { kind: o.value })}
                  />
                </div>
                <button type="button" className="gmbw-mp-x" title="Remove column" onClick={() => removeCol(i)}>×</button>
              </div>

              {perWs.map((w) => (
                <div className="gmbw-mp-src" key={w.id}>
                  <span className="gmbw-mp-ws">{w.name}</span>
                  <div className="gmbw-mp-src-fields">
                    <LightSelect
                      width="100%"
                      height={34}
                      fontSize={12.5}
                      radius={8}
                      menuWidth={300}
                      placeholder="— none —"
                      value={col.sources?.[w.id] || ""}
                      options={metricOptions(w.id)}
                      onChange={(o) => pickSource(i, w.id, o.value)}
                    />
                    {col.sources?.[w.id] && (
                      <SourceWindowField
                        value={col.sourceWindows?.[w.id] || null}
                        onChange={(sw) => patchSourceWindow(i, w.id, sw)}
                      />
                    )}
                  </div>
                </div>
              ))}

              <div className="gmbw-mp-mode">
                <LightSelect
                  width="100%"
                  height={34}
                  fontSize={12.5}
                  radius={8}
                  value={col.mode === "sum" ? "sum" : "avg"}
                  options={[
                    { value: "avg", label: "Average across workspaces" },
                    { value: "sum", label: "Sum across workspaces" },
                  ]}
                  onChange={(o) => patchCol(i, { mode: o.value })}
                />
              </div>
            </div>
          ))}
          <button type="button" className="gmbw-mp-add" onClick={addCol}>+ Add a column</button>
          <p className="gmbw-hint">
            Each column pools the metric you pick from every workspace — averaged (rates, tickets) or summed (counts, revenue).
            A workspace left on “none” simply doesn’t feed that column. Per-metric date filters override the board filter for that source only.
          </p>
        </div>
      )}
    </div>
  );
};

MetricPlanEditor.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  draft: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  minSeedCols: PropTypes.number,
  // eslint-disable-next-line react/forbid-prop-types
  metricCatalog: PropTypes.array,
};
MetricPlanEditor.defaultProps = {
  minSeedCols: 1,
  metricCatalog: [],
};

/* Re-export so the drawer can decide when to show the builder. */
export { mpNameKey };

export default MetricPlanEditor;
